// The widget is generated by JAMES (ada-web, POST /api/ai/custom-widget), so the
// generation instructions never reach the browser. This module finds that endpoint,
// builds the request (new widget or refinement) and maps the result onto the entry.
//
// Kept free of runtime imports so tests/unit can run it with plain node.

import type { EntryToolContext } from '../../ai/tools/entry'

export type GeneratedWidget = {
	template: string,
	translations: { [locale: string]: { [key: string]: string } },
}

type HistoryEntry = { prompt: string, createdAt?: string }

type PreviewEnvironment = { sys: { id: string }, baseUrl?: string }

// The same source the live preview uses: the selected preview environment points
// at the JAMES site. A localStorage override allows pointing at a local ada-web.
export function jamesOrigin(previews: PreviewEnvironment[], selectedId?: string, override?: string): string | null {
	const url = override || (previews.find(p => p.sys.id == selectedId) ?? previews[0])?.baseUrl
	if (!url) return null
	try {
		const origin = new URL(url).origin
		// a baseUrl that is a template in its host part has no usable origin
		return origin.includes('{') ? null : origin
	}
	catch {
		return null
	}
}

// The widget as it is now, including manual edits - what a refinement builds on.
export function currentWidget(fields: any, defaultLocale: string): GeneratedWidget | undefined {
	const template = fields?.customCode?.[defaultLocale]
	if (!template) return undefined
	const translations = Object.fromEntries(Object.entries(fields.translations ?? {})
		.filter(([ , value ]) => value && typeof value == 'object')) as GeneratedWidget['translations']
	return { template, translations }
}

// conversationHistory holds the prompts that shaped the widget, never model output.
// Entries written by the old generate-ai-widget script are API messages - only
// their user prompts are kept.
export function readHistory(value: any): HistoryEntry[] {
	if (!Array.isArray(value)) return []
	return value.flatMap(item =>
		typeof item?.prompt == 'string' ? [ { prompt: item.prompt, createdAt: item.createdAt } ] :
		item?.role == 'user' && typeof item.content == 'string' ? [ { prompt: item.content } ] :
		[])
}

// JAMES accepts at most this many earlier prompts. The entry keeps the full history,
// the request only carries the most recent ones.
const MAX_REQUEST_HISTORY = 50

export function historyForRequest(history: HistoryEntry[]): string[] {
	return history.slice(-MAX_REQUEST_HISTORY).map(entry => entry.prompt)
}

type WidgetRequestBody = {
	prompt: string,
	spaceId: string,
	// the entry's locale codes - JAMES translates into exactly these
	locales: string[],
	current?: GeneratedWidget,
	history?: string[],
}

type Connection = {
	// JAMES origin, see jamesOrigin()
	origin: string,
	// Contenthub token - JAMES verifies it against the CMA
	token: string,
	apiKey?: string,
	signal?: AbortSignal,
}

export async function requestWidget(body: WidgetRequestBody, { origin, token, apiKey, signal }: Connection): Promise<GeneratedWidget> {
	const headers = {
		'content-type': 'application/json',
		authorization: 'Bearer ' + token,
	}

	// The BYOK key goes in the body, not a custom header: the JAMES ingress answers CORS
	// preflights itself and only allows a fixed set of standard headers.
	const payload = apiKey ? { ...body, apiKey } : body
	const response = await fetch(origin + '/api/ai/custom-widget', { method: 'POST', headers, body: JSON.stringify(payload), signal })
	const result = await response.json().catch(() => null)
	if (!response.ok) throw new Error(result?.message ?? response.status + ' ' + response.statusText)
	if (!isWidget(result, body.locales)) throw new Error('JAMES returned an incomplete widget - the entry was not changed.')
	return result
}

// Checked before anything is written, so a broken response never leaves the entry half updated.
function isWidget(value: any, locales: string[]): value is GeneratedWidget {
	return typeof value?.template == 'string' && !!value.template
		&& locales.every(locale => value.translations?.[locale] && typeof value.translations[locale] == 'object')
}

type WidgetEnv = Connection & {
	entryApi: Pick<EntryToolContext, 'getEntry' | 'getLocaleCodes' | 'defaultLocale' | 'setField'>,
	spaceId: string,
}

// Generates a new widget, or refines the entry's current one, and writes customCode,
// translations and conversationHistory into the entry. Returns a short summary for the
// chat - never the widget code, which stays out of the conversation.
export async function generateIntoEntry(prompt: string, startFromScratch: boolean, env: WidgetEnv): Promise<string> {
	const { entryApi } = env
	const fields = entryApi.getEntry()?.fields
	const current = startFromScratch ? undefined : currentWidget(fields, entryApi.defaultLocale)
	// the prompts that shaped the current widget - without one (new, cleared by hand or
	// start_from_scratch) the history starts over, so it never describes another widget
	const earlier = current ? readHistory(fields?.conversationHistory?.[entryApi.defaultLocale]) : []
	const locales = entryApi.getLocaleCodes()

	const widget = await requestWidget({
		prompt,
		spaceId: env.spaceId,
		locales,
		...(current ? { current, history: historyForRequest(earlier) } : {}),
	}, env)
	// stopped after the response arrived: still leave the entry unchanged
	env.signal?.throwIfAborted()

	// customCode and conversationHistory are one value for all languages, translations
	// is localized; requestWidget() made sure every entry locale is there
	const history = [ ...earlier, { prompt, createdAt: new Date().toISOString() } ]
	entryApi.setField('customCode', entryApi.defaultLocale, widget.template)
	entryApi.setField('conversationHistory', entryApi.defaultLocale, history)
	for (const locale of locales) entryApi.setField('translations', locale, widget.translations[locale])
	return `${ current ? 'Refined the existing widget' : 'Generated a new widget' }: customCode, translations (${ locales.join(', ') }) and conversationHistory were updated and autosaved. The Preview tab shows the result.`
}
